"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import type { JSX, RefObject } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ProductIcon } from "./Sections";
import { products } from "./products";
import { useI18n } from "@/lib/i18n/I18nProvider";

gsap.registerPlugin(ScrollTrigger);

/* Five-tile product bento (usd.ai layout, behaviour and motion rhythm; all
   artwork original). Each illustration runs a continuous GSAP scene while its
   tile is on screen:
   - rack     indicators change state in batches; allocation sweeps a column
   - cards    a carousel of accelerator cards slides left, fans spinning
   - shell    a terminal types commands, prints output and scrolls
   - stacks   plates are dealt onto three piles, then lifted off again
   - globe    the node sphere turns while signals pulse on its front face   */

type ArtKey = "rack" | "cards" | "shell" | "stacks" | "globe";

const TILES: { slug: string; art: ArtKey; icon: number }[] = [
  { slug: "virtual-machines", art: "rack", icon: 0 },
  { slug: "functions", art: "cards", icon: 2 },
  { slug: "cloud-shell", art: "shell", icon: 5 },
  { slug: "containers", art: "stacks", icon: 1 },
  { slug: "networking", art: "globe", icon: 3 },
];

const f1 = (n: number) => n.toFixed(1);

/* ---- 1. Rack ------------------------------------------------------------- */
const RACK_COLS = 3;
const RACK_ROWS = 9;

function RackArt() {
  const units = [];
  for (let r = 0; r < RACK_ROWS; r++) {
    for (let c = 0; c < RACK_COLS; c++) {
      units.push(
        <g
          key={`${r}-${c}`}
          className="dc-rack-unit"
          data-col={c}
          data-row={r}
          transform={`translate(${28 + c * 207} ${28 + r * 33})`}
        >
          <rect className="dc-unit" width="188" height="25" rx="2" />
          <rect className="dc-led dc-led-a" x="7" y="7" width="10" height="4" rx="1" />
          <rect className="dc-led dc-led-b" x="7" y="14" width="10" height="4" rx="1" />
          <line className="dc-vent" x1="25" y1="9" x2="180" y2="9" />
          <line className="dc-vent" x1="25" y1="13" x2="180" y2="13" />
          <line className="dc-vent" x1="25" y1="17" x2="180" y2="17" />
        </g>,
      );
    }
  }
  return (
    <svg className="dc-art dc-art-rack" viewBox="0 0 660 330" aria-hidden="true">
      <rect className="dc-chassis" x="0.5" y="0.5" width="659" height="360" rx="4" />
      {units}
    </svg>
  );
}

/* ---- 2. Card carousel ------------------------------------------------------ */
const CARD_PITCH = 450;

function Card({ x }: { x: number }) {
  const ticks = Array.from({ length: 96 }, (_, i) => (i * 360) / 96);
  const fingers = Array.from({ length: 36 }, (_, i) => i);
  return (
    <g transform={`translate(${x} 0)`}>
      <rect className="dc-board" x="0" y="0.5" width="404" height="200" />
      <rect className="dc-fingers-box" x="10" y="200" width="146" height="16" />
      {fingers.map((i) => (
        <line key={i} className="dc-finger" x1={13 + i * 4} y1="202" x2={13 + i * 4} y2="214" />
      ))}
      <g transform="translate(306 100)">
        <circle className="dc-fan-disc" r="78" />
        <g className="dc-fan-blades">
          {ticks.map((a) => (
            <line
              key={a}
              className="dc-fan-tick"
              x1="0"
              y1="-58"
              x2="0"
              y2="-70"
              transform={`rotate(${a}) rotate(14 0 -64)`}
            />
          ))}
        </g>
        <circle className="dc-fan-hub" r="56" />
      </g>
    </g>
  );
}

function CardsArt() {
  return (
    <svg className="dc-art dc-art-cards" viewBox="0 0 490 230" aria-hidden="true">
      <g className="dc-cards">
        {[-10, -10 + CARD_PITCH, -10 + CARD_PITCH * 2].map((x) => (
          <Card key={x} x={x} />
        ))}
      </g>
    </svg>
  );
}

/* ---- 3. Shell: a terminal session ----------------------------------------- */
type Row = { kind: "cmd" | "out"; w: number; level?: "ok" | "warn" | "info" };

const SESSION: Row[] = [
  { kind: "cmd", w: 132 },
  { kind: "out", w: 210, level: "info" },
  { kind: "out", w: 168, level: "ok" },
  { kind: "cmd", w: 96 },
  { kind: "out", w: 240, level: "ok" },
  { kind: "out", w: 190, level: "ok" },
  { kind: "out", w: 120, level: "warn" },
  { kind: "cmd", w: 150 },
  { kind: "out", w: 220, level: "info" },
  { kind: "out", w: 260, level: "ok" },
  { kind: "cmd", w: 84 },
  { kind: "out", w: 200, level: "ok" },
  { kind: "out", w: 150, level: "ok" },
  { kind: "out", w: 230, level: "info" },
  { kind: "cmd", w: 118 },
  { kind: "out", w: 180, level: "ok" },
  { kind: "out", w: 250, level: "ok" },
  { kind: "cmd", w: 104 },
  { kind: "out", w: 210, level: "ok" },
  { kind: "out", w: 140, level: "warn" },
];
const SH_TOP = 66;
const SH_LINE = 24;
const SH_ROWS = 6; // rows visible below the copy before the log scrolls
const SH_X = 78;

function ShellArt() {
  return (
    <svg className="dc-art dc-art-shell" viewBox="0 0 480 330" aria-hidden="true">
      <defs>
        <clipPath id="dc-shell-clip">
          <rect x="40" y="44" width="520" height="300" />
        </clipPath>
      </defs>
      {/* window behind: a second session peeking out */}
      <rect className="dc-win dc-win-back" x="78" y="-22" width="480" height="60" rx="8" />
      <rect className="dc-win" x="40" y="0.5" width="520" height="400" rx="8" />
      <line className="dc-win-rule" x1="40" y1="40" x2="560" y2="40" />
      <circle className="dc-win-dot" cx="62" cy="20.5" r="4.5" />
      <circle className="dc-win-dot" cx="78" cy="20.5" r="4.5" />
      <circle className="dc-win-dot" cx="94" cy="20.5" r="4.5" />
      <path className="dc-tab" d="M122 40 v-22 a5 5 0 0 1 5 -5 h110 a5 5 0 0 1 5 5 v22" />
      <line className="dc-tab-text" x1="138" y1="27" x2="210" y2="27" />
      <line className="dc-tab-text dc-tab-text-muted" x1="262" y1="27" x2="318" y2="27" />
      <g clipPath="url(#dc-shell-clip)">
        <g className="dc-shell-log">
          {SESSION.map((row, i) => {
            const y = SH_TOP + i * SH_LINE;
            return row.kind === "cmd" ? (
              <g key={i} className="dc-shell-row dc-shell-cmd" data-w={row.w}>
                <path className="dc-prompt" d={`M${SH_X - 16} ${y - 5} l6 5 -6 5`} />
                <line className="dc-cmd-text" x1={SH_X} y1={y} x2={SH_X + row.w} y2={y} />
              </g>
            ) : (
              <g key={i} className="dc-shell-row dc-shell-out">
                <circle className={`dc-out-dot is-${row.level}`} cx={SH_X - 13} cy={y} r="3" />
                <line className="dc-out-text" x1={SH_X} y1={y} x2={SH_X + row.w} y2={y} />
              </g>
            );
          })}
          <rect className="dc-caret" x={SH_X} y={SH_TOP - 7} width="8" height="14" rx="1" />
        </g>
      </g>
    </svg>
  );
}

/* ---- 4. Isometric stacks ---------------------------------------------------- */
const SLAB_STEP = 3.2;

function slabTop(cx: number, cy: number, a: number) {
  const b = a * 0.58;
  return `${cx},${cy - b} ${cx + a},${cy} ${cx},${cy + b} ${cx - a},${cy}`;
}

function Slab({ cx, cy, a, layers }: { cx: number; cy: number; a: number; layers: number }) {
  const b = a * 0.58;
  const depth = layers * SLAB_STEP;
  return (
    <g>
      <polygon
        className="dc-slab-side"
        points={`${cx - a},${cy} ${cx},${cy + b} ${cx + a},${cy} ${cx + a},${cy + depth} ${cx},${cy + b + depth} ${cx - a},${cy + depth}`}
      />
      {Array.from({ length: layers }, (_, i) => (i + 1) * SLAB_STEP).map((dy) => (
        <polyline key={dy} className="dc-slab-edge" points={`${cx - a},${cy + dy} ${cx},${cy + b + dy} ${cx + a},${cy + dy}`} />
      ))}
      <polygon className="dc-slab-top" points={slabTop(cx, cy, a)} />
      <ellipse className="dc-slab-mark" cx={cx} cy={cy} rx={a * 0.32} ry={a * 0.2} />
    </g>
  );
}

const PILES = [
  { cx: 430, cy: 200, a: 170, layers: 7 },
  { cx: 250, cy: 320, a: 170, layers: 8 },
  { cx: 90, cy: 460, a: 170, layers: 6 },
];
const DEALT = 3;

function StacksArt() {
  return (
    <svg className="dc-art dc-art-stacks" viewBox="0 0 480 320" aria-hidden="true">
      {PILES.map((p, i) => (
        <g key={i} className="dc-pile">
          <Slab {...p} />
          {Array.from({ length: DEALT }, (_, k) => (
            <g key={k} className="dc-plate" data-pile={i} data-k={k}>
              <Slab cx={p.cx} cy={p.cy - (k + 1) * SLAB_STEP * 1.6} a={p.a} layers={1} />
            </g>
          ))}
        </g>
      ))}
    </svg>
  );
}

/* ---- 5. Node globe ------------------------------------------------------------ */
type V3 = [number, number, number];
const G_R = 300;
const G_CX = 330;
const G_CY = 330;
const G_TILT = 0.38;

const GLOBE = (() => {
  const n = 80;
  const golden = Math.PI * (3 - Math.sqrt(5));
  const pts: V3[] = Array.from({ length: n }, (_, i) => {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    return [Math.cos(golden * i) * r, y, Math.sin(golden * i) * r];
  });
  const seen = new Set<string>();
  const edges: [number, number][] = [];
  pts.forEach((p, i) => {
    pts
      .map((q, j) => [j, (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2 + (p[2] - q[2]) ** 2] as const)
      .filter(([j]) => j !== i)
      .sort((x, y) => x[1] - y[1])
      .slice(0, 4)
      .forEach(([j]) => {
        const k = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (!seen.has(k)) {
          seen.add(k);
          edges.push([i, j]);
        }
      });
  });
  return { pts, edges };
})();

function project(p: V3, angle: number) {
  const ca = Math.cos(angle), sa = Math.sin(angle);
  const x1 = p[0] * ca + p[2] * sa;
  const z1 = -p[0] * sa + p[2] * ca;
  const ct = Math.cos(G_TILT), st = Math.sin(G_TILT);
  return { x: G_CX + x1 * G_R, y: G_CY - (p[1] * ct - z1 * st) * G_R, z: p[1] * st + z1 * ct };
}

function GlobeArt() {
  const q = GLOBE.pts.map((p) => project(p, 0));
  return (
    <svg className="dc-art dc-art-globe" viewBox="0 0 520 380" aria-hidden="true">
      <circle className="dc-globe-rim" cx={G_CX} cy={G_CY} r={G_R} />
      {GLOBE.edges.map(([a, b], i) => (
        <line
          key={i}
          className="dc-globe-edge"
          x1={f1(q[a].x)}
          y1={f1(q[a].y)}
          x2={f1(q[b].x)}
          y2={f1(q[b].y)}
          opacity={(q[a].z + q[b].z) / 2 > 0 ? "1" : "0.16"}
        />
      ))}
      {q.map((v, i) => (
        <rect
          key={i}
          className="dc-globe-node"
          x={f1(v.x - 3.5)}
          y={f1(v.y - 3.5)}
          width="7"
          height="7"
          rx="1"
          opacity={v.z > 0 ? "1" : "0.12"}
        />
      ))}
      {[0, 1, 2].map((i) => (
        <circle key={i} className="dc-globe-pulse" cx="-50" cy="-50" r="4" opacity="0" />
      ))}
      <circle className="dc-globe-signal" cx="-50" cy="-50" r="3.5" opacity="0" />
    </svg>
  );
}

const ART: Record<ArtKey, () => JSX.Element> = {
  rack: RackArt,
  cards: CardsArt,
  shell: ShellArt,
  stacks: StacksArt,
  globe: GlobeArt,
};

/* ---- Motion --------------------------------------------------------------------- */

function onScreen(el: Element, loops: gsap.core.Animation[]) {
  ScrollTrigger.create({
    trigger: el,
    start: "top bottom",
    end: "bottom top",
    onToggle: (s) => loops.forEach((l) => (s.isActive ? l.play() : l.pause())),
  });
}

const pick = <T,>(list: T[]) => list[Math.floor(Math.random() * list.length)];

/** GSAP cannot interpolate var(); read the token's resolved colour instead. */
const token = (el: Element, name: string) =>
  getComputedStyle(el).getPropertyValue(name).trim() || "#ffffff";

function rackScene(tile: Element) {
  const leds = gsap.utils.toArray<SVGRectElement>(tile.querySelectorAll(".dc-led"));
  const units = gsap.utils.toArray<SVGGElement>(tile.querySelectorAll(".dc-rack-unit"));
  const shades = ["--dc-paper", "--dc-fill", "--dc-fill-strong", "--dc-mark", "--dc-on"].map((n) => token(tile, n));
  const hot = token(tile, "--dc-node");
  const cool = token(tile, "--dc-fill-strong");
  const edge = token(tile, "--dc-line");

  // Batches of indicators change state every half second.
  const flicker = gsap.timeline({ paused: true, repeat: -1 });
  flicker
    .call(() => {
      for (let i = 0; i < 6; i++) gsap.to(pick(leds), { fill: pick(shades), duration: 0.25, ease: "power1.out" });
    })
    .to({}, { duration: 0.5 });

  // Allocation: a column lights unit by unit, holds, then settles.
  const sweep = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 1.6 });
  sweep.call(() => {
    const col = Math.floor(Math.random() * RACK_COLS);
    const column = units.filter((u) => u.dataset.col === String(col));
    const start = Math.floor(Math.random() * 3);
    const run = column.slice(start, start + 4 + Math.floor(Math.random() * 4));
    const tl = gsap.timeline();
    run.forEach((u, i) => {
      tl.to(u.querySelectorAll(".dc-vent"), { stroke: hot, duration: 0.18, ease: "power1.out" }, i * 0.09);
      tl.to(u.querySelector(".dc-unit"), { stroke: edge, duration: 0.18 }, i * 0.09);
    });
    tl.to(run.flatMap((u) => [...u.querySelectorAll(".dc-vent")]), { stroke: cool, duration: 0.6, ease: "power2.inOut" }, "+=1.1");
    tl.to(run.map((u) => u.querySelector(".dc-unit")), { stroke: cool, duration: 0.6 }, "<");
  });
  sweep.to({}, { duration: 2.4 });
  return [flicker, sweep];
}

function cardsScene(tile: Element) {
  const track = tile.querySelector(".dc-cards");
  const fans = gsap.utils.toArray<SVGGElement>(tile.querySelectorAll(".dc-fan-blades"));
  const spin = gsap.timeline({ paused: true });
  fans.forEach((fan) =>
    spin.to(fan, { rotation: 360, svgOrigin: "0 0", duration: 2.2, ease: "none", repeat: -1 }, 0),
  );
  // Carousel: hold, then glide one card to the left (seamless: 3 identical cards).
  const slide = gsap.fromTo(
    track,
    { x: 0 },
    { x: -CARD_PITCH, duration: 1.3, ease: "power3.inOut", repeat: -1, repeatDelay: 2.4, delay: 1.6, paused: true },
  );
  return [spin, slide];
}

function shellScene(tile: Element) {
  const log = tile.querySelector<SVGGElement>(".dc-shell-log");
  const rows = gsap.utils.toArray<SVGGElement>(tile.querySelectorAll(".dc-shell-row"));
  const caret = tile.querySelector(".dc-caret");
  if (!log || !caret) return [];
  const tl = gsap.timeline({ paused: true, repeat: -1 });
  tl.set(rows, { opacity: 0 }).set(log, { y: 0 }).set(caret, { opacity: 1, x: 0, y: 0 });
  const blink = gsap.to(caret, { opacity: 0, duration: 0.45, repeat: -1, yoyo: true, ease: "steps(1)", paused: true });

  SESSION.forEach((row, i) => {
    const el = rows[i];
    const y = i * SH_LINE;
    const scroll = i >= SH_ROWS ? -(i - SH_ROWS + 1) * SH_LINE : 0;
    if (scroll) tl.to(log, { y: scroll, duration: 0.28, ease: "power2.out" });
    if (row.kind === "cmd") {
      const steps = Math.max(4, Math.round(row.w / 9));
      const text = el.querySelector(".dc-cmd-text");
      tl.set(caret, { y, x: 0 })
        .to({}, { duration: 0.5 }) // waiting at the prompt
        .set(el, { opacity: 1 })
        .fromTo(text, { scaleX: 0 }, { scaleX: 1, transformOrigin: "0% 50%", duration: steps * 0.055, ease: `steps(${steps})` })
        .to(caret, { x: row.w + 6, duration: steps * 0.055, ease: `steps(${steps})` }, "<")
        .to({}, { duration: 0.35 }); // "enter"
    } else {
      tl.set(caret, { y: y + SH_LINE, x: 0 })
        .fromTo(el, { opacity: 0, x: -6 }, { opacity: 1, x: 0, duration: 0.18, ease: "power2.out" })
        .to({}, { duration: 0.09 });
    }
  });
  tl.to({}, { duration: 1.6 }).to(rows, { opacity: 0, duration: 0.4, ease: "power1.in" });
  return [tl, blink];
}

function stacksScene(tile: Element) {
  const plates = gsap.utils.toArray<SVGGElement>(tile.querySelectorAll(".dc-plate"));
  // Deal order: round-robin over the piles, bottom plate first.
  const order = [...plates].sort(
    (a, b) => Number(a.dataset.k) - Number(b.dataset.k) || Number(a.dataset.pile) - Number(b.dataset.pile),
  );
  const tl = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 0.4 });
  tl.set(plates, { opacity: 0, y: -40 });
  order.forEach((p) => {
    tl.to(p, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, "+=0.18");
  });
  tl.to({}, { duration: 1.4 });
  [...order].reverse().forEach((p, i) => {
    tl.to(p, { opacity: 0, y: -40, duration: 0.45, ease: "power2.in" }, i === 0 ? ">" : "<0.12");
  });
  const bob = gsap.timeline({ paused: true });
  tile.querySelectorAll(".dc-pile").forEach((pile, i) => {
    bob.to(pile, { y: -4, duration: 2.6 + i * 0.5, ease: "sine.inOut", repeat: -1, yoyo: true }, i * 0.4);
  });
  return [tl, bob];
}

function globeScene(tile: Element) {
  const edges = gsap.utils.toArray<SVGLineElement>(tile.querySelectorAll(".dc-globe-edge"));
  const nodes = gsap.utils.toArray<SVGRectElement>(tile.querySelectorAll(".dc-globe-node"));
  const pulses = gsap.utils.toArray<SVGCircleElement>(tile.querySelectorAll(".dc-globe-pulse"));
  const signal = tile.querySelector<SVGCircleElement>(".dc-globe-signal");
  const state = { a: 0 };
  let proj = GLOBE.pts.map((p) => project(p, 0));
  const spin = gsap.to(state, {
    a: Math.PI * 2,
    duration: 70,
    ease: "none",
    repeat: -1,
    paused: true,
    onUpdate: () => {
      proj = GLOBE.pts.map((p) => project(p, state.a));
      GLOBE.edges.forEach(([a, b], i) => {
        const l = edges[i];
        l.setAttribute("x1", f1(proj[a].x));
        l.setAttribute("y1", f1(proj[a].y));
        l.setAttribute("x2", f1(proj[b].x));
        l.setAttribute("y2", f1(proj[b].y));
        l.setAttribute("opacity", (proj[a].z + proj[b].z) / 2 > 0 ? "1" : "0.16");
      });
      proj.forEach((v, i) => {
        nodes[i].setAttribute("x", f1(v.x - 3.5));
        nodes[i].setAttribute("y", f1(v.y - 3.5));
        nodes[i].setAttribute("opacity", v.z > 0 ? "1" : "0.12");
      });
    },
  });
  // Front-face nodes ping, and a signal hops along a visible edge.
  let n = 0;
  const ping = gsap.timeline({ paused: true, repeat: -1 });
  ping
    .call(() => {
      const front = proj.map((v, i) => [v, i] as const).filter(([v]) => v.z > 0.25);
      if (!front.length) return;
      const [v] = pick(front);
      const c = pulses[n++ % pulses.length];
      gsap.fromTo(c, { attr: { cx: v.x, cy: v.y, r: 4 }, opacity: 0.9 }, { attr: { r: 18 }, opacity: 0, duration: 1.1, ease: "power2.out" });
      if (signal) {
        const vis = GLOBE.edges.filter(([a, b]) => proj[a].z > 0.2 && proj[b].z > 0.2);
        if (vis.length) {
          const [a, b] = pick(vis);
          const from = proj[a], to = proj[b];
          gsap.fromTo(
            signal,
            { attr: { cx: from.x, cy: from.y }, opacity: 1 },
            { attr: { cx: to.x, cy: to.y }, duration: 0.7, ease: "power1.inOut", onComplete: () => void gsap.to(signal, { opacity: 0, duration: 0.2 }) },
          );
        }
      }
    })
    .to({}, { duration: 0.55 });
  return [spin, ping];
}

const SCENES: Record<ArtKey, (tile: Element) => gsap.core.Animation[]> = {
  rack: rackScene,
  cards: cardsScene,
  shell: shellScene,
  stacks: stacksScene,
  globe: globeScene,
};

function useBentoMotion(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      // Entrance fades only: tiles never move, so rows cannot end up offset.
      gsap.fromTo(
        ".dc-tile",
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.7,
          ease: "power2.out",
          stagger: 0.07,
          clearProps: "opacity",
          scrollTrigger: { trigger: el.querySelector(".dc-grid"), start: "top 85%", once: true },
        },
      );
      (Object.keys(SCENES) as ArtKey[]).forEach((key) => {
        const tile = el.querySelector(`.dc-tile-${key}`);
        if (tile) onScreen(tile, SCENES[key](tile));
      });
    }, el);
    return () => ctx.revert();
  }, [root]);
}

/* ---- Section ---------------------------------------------------------------------- */

function Arrow() {
  return (
    <span className="dc-arrow" aria-hidden="true">
      <svg viewBox="0 0 16 16">
        <path d="M5.5 10.5 10.5 5.5M6.5 5.5h4v4" />
      </svg>
    </span>
  );
}

export function InfrastructureDecisions() {
  const root = useRef<HTMLElement>(null);
  const { t } = useI18n();
  const copy = t.landing.decisions;
  useBentoMotion(root);
  return (
    <section className="ed-section ed-business-intro dc" ref={root}>
      <div className="ec-container">
        <header className="dc-head">
          <p className="ec-eyebrow">{copy.eyebrow}</p>
          <h2>
            {copy.title} <span className="ec-h2-sub">{copy.sub}</span>
          </h2>
        </header>
        <ul className="dc-grid">
          {TILES.map(({ slug, art, icon }) => {
            const product = products.find((p) => p.slug === slug);
            if (!product) return null;
            const local = t.products[slug as keyof typeof t.products];
            const name = local?.name ?? product.name;
            const short = local?.short ?? product.short;
            const Art = ART[art];
            return (
              <li key={slug} className={`dc-cell dc-cell-${art}`}>
                <Link
                  href={`/products/${slug}`}
                  className={`dc-tile dc-tile-${art}`}
                  aria-label={`${name}: ${short}`}
                >
                  <div className="dc-title-row">
                    <span className="dc-icon" aria-hidden="true">
                      <ProductIcon index={icon} />
                    </span>
                    <h3>{name}</h3>
                    <Arrow />
                  </div>
                  <p>{short}</p>
                  <Art />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
